/**
 * The cache on the loading scene: a step that loads files through the cache (assets.ts) reports
 * their bytes as its progress, and the scene says whether they came from the cache in this browser
 * or are being downloaded, and how much.
 */
import type { Report } from '../loading/loader';
import { type AssetTransfer, watchAssets } from './assets';
import { megabytes } from './lru';

/** Where the loading scene shows the line (loading/scene.ts). */
export interface SourceView {
  source(text: string | null): void;
}

/** The line for what has loaded so far (Thai), or null when nothing went through the cache. */
export function sourceLine(transfers: Iterable<AssetTransfer>): string | null {
  let kept = 0;
  let keptBytes = 0;
  let down = 0;
  let got = 0;
  let of = 0;
  let pending = false;
  for (const t of transfers) {
    if (t.from === 'cache') {
      kept++;
      keptBytes += t.total;
    } else if (t.total || !t.done) {
      down++;
      got += t.loaded;
      of += t.total;
      pending ||= !t.done;
    }
  }
  const fromCache = kept ? `จากแคชในเครื่อง ${kept} ไฟล์ (${megabytes(keptBytes)} MB)` : '';
  if (!down) return kept ? `โหลด${fromCache}` : null;
  const net = pending
    ? `กำลังดาวน์โหลด ${megabytes(got)} / ${megabytes(of)} MB`
    : `ดาวน์โหลดแล้ว ${megabytes(of)} MB`;
  return fromCache ? `${net} · ${fromCache}` : net;
}

/**
 * Runs `work`, reporting the bytes of the files it loads through the cache as its progress and
 * naming their source on `view`. The line stays once done, until the scene hides.
 */
export async function trackAssets<T>(report: Report, view: SourceView, work: () => Promise<T>): Promise<T> {
  const seen = new Map<string, AssetTransfer>();
  const show = () => {
    let loaded = 0;
    let total = 0;
    for (const t of seen.values()) {
      loaded += t.loaded;
      total += t.total;
    }
    if (total) report(loaded / total);
    view.source(sourceLine(seen.values()));
  };
  const stop = watchAssets((t) => {
    seen.set(t.path, t);
    show();
  });
  try {
    return await work();
  } finally {
    stop();
    show();
  }
}
